/**
 * `Operação · Organização · ajustar` (Figma `356:13656`), the dialog frame
 * at `356:13755`.
 *
 * `POST /ops/tenants/{tenantId}/slot-movements` has no shared home yet:
 * `src/ops/api/{schemas,queries}.ts` belong to no task in this plan, so the
 * schema and the mutation are local to this file and marked ASSUMED, the
 * same shape `[local-hook-assumed]` names. `slotMovementSchema` transcribes
 * `SlotMovementOut` from `2026-09-18-fs-campaign-capacity.md` Task CA-3's
 * Interfaces block field for field; `delta`/`reason` transcribe
 * `SlotMovementIn`. CA-8's end-to-end run against the real route is what
 * proves the two agree.
 *
 * `campaignSlots`/`campaignsInUse` arrive as props, the same split the
 * credits mutation below uses: this component owns the adjustment action,
 * not the tenant fetch.
 *
 * `campaignsInUse` stays typed `number | null` for a caller with no
 * operator-readable source, though `TenantDetail` now always has one
 * (`OpsTenantDetail.campaigns_in_use`, never null). Null suppresses the
 * below-use warning rather than risking a false negative from a count that
 * would otherwise read 0.
 *
 * The frame's `Segmented/Grandeza` control offers "Vagas de campanha" and
 * "Créditos" as one dialog's two faces, each with its own fields, mutation
 * and reset -- switching tabs never carries a value from one face into the
 * other. `initialTab` lets a caller open straight into either face (the
 * Capacidade panel's own two "Ajustar" triggers, the page header's shared
 * one) without the dialog remembering the last face a previous open used --
 * `TenantDetail` remounts this component per open by keying it on which
 * trigger fired.
 */

import { useId, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { ChevronRight, Minus, Plus } from 'lucide-react';
import { z } from 'zod';

import { defineCatalogue, getLocale } from '../../i18n';
import { integer } from '../../i18n/format';
import { Absent } from '../../chrome/measure';
import { Callout } from '../../chrome/Callout';
import { FormDialog, fieldErrorOf, fieldControlProps } from '../../chrome/form';
import { IconHint } from '../../chrome/hint';
import { useApiClient } from '../../app/session/client';
import type { ApiFailureError } from '../../lib/http/errors';
import { opsKeys, useAdjustCredits } from '../api/queries';
import { AmountField, type Sign } from '../credits/AmountField';
import { Button } from '../../ui/button';
import { Field, FieldError, FieldLegend, FieldSet, FieldLabel, FieldDescription } from '@/ui/field';
import { Input } from '@/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/tabs';

const catalogue = defineCatalogue(
  {
    title: 'Ajustar capacidade',
    'grandeza.legend': 'O que ajustar',
    'tab.slots': 'Vagas de campanha',
    'tab.credits': 'Créditos',
    'movement.legend': 'Movimento',
    'direction.increase': 'Aumentar em',
    'direction.decrease': 'Reduzir em',
    'stepper.decrement': 'Diminuir a quantidade',
    'stepper.increment': 'Aumentar a quantidade',
    'result.now': 'agora',
    'result.after': 'depois do ajuste',
    'result.slots.other': '{n} vagas',
    'result.slots.one': '{n} vaga',
    'result.absent': 'Esta organização ainda não tem vagas registradas.',
    'warning.other': 'Usa {inUse} vagas e ficará com {after}. Nenhuma campanha para de coletar.',
    'warning.one': 'Usa {inUse} vagas e ficará com {after}. Nenhuma campanha para de coletar.',
    'warning.unknown':
      'O uso atual de vagas não está disponível, então a redução não pode ser comparada com ele.',
    'reason.label': 'Motivo',
    'credits.amount.hint': 'Corrige o saldo para cima ou para baixo.',
    'credits.note.label': 'Nota',
    'credits.note.hint': 'O motivo de corrigir o saldo, para o registro permanente.',
    'submit.slots': 'Registrar ajuste',
    'submit.credits': 'Ajustar',
  },
  {
    title: 'Adjust capacity',
    'grandeza.legend': 'What to adjust',
    'tab.slots': 'Campaign slots',
    'tab.credits': 'Credits',
    'movement.legend': 'Movement',
    'direction.increase': 'Increase by',
    'direction.decrease': 'Reduce by',
    'stepper.decrement': 'Decrease the amount',
    'stepper.increment': 'Increase the amount',
    'result.now': 'now',
    'result.after': 'after the adjustment',
    'result.slots.other': '{n} slots',
    'result.slots.one': '{n} slot',
    'result.absent': 'This organisation has no slots on record yet.',
    'warning.other': 'Uses {inUse} slots and will have {after}. No campaign stops collecting.',
    'warning.one': 'Uses {inUse} slots and will have {after}. No campaign stops collecting.',
    'warning.unknown':
      "Current slot usage isn't available, so the reduction can't be checked against it.",
    'reason.label': 'Reason',
    'credits.amount.hint': 'Corrects the balance up or down.',
    'credits.note.label': 'Note',
    'credits.note.hint': 'The reason you are correcting the balance, for the permanent ledger.',
    'submit.slots': 'Register adjustment',
    'submit.credits': 'Adjust',
  },
);
const t = catalogue.t;

/** `SlotMovementOut`, CA-3's Interfaces block. */
const slotMovementSchema = z.object({
  id: z.string(),
  slots: z.number().int(),
  reason: z.string(),
  occurred_at: z.string(),
  actor_user_id: z.string(),
});
type SlotMovement = z.infer<typeof slotMovementSchema>;

type Direction = 'increase' | 'decrease';

interface AdjustSlotsInput {
  delta: number;
  reason: string;
}

/** `POST /ops/tenants/{t}/slot-movements  {delta, reason}` -- ASSUMED, see
 *  the module doc. `delta` already carries its sign, resolved from the
 *  dialog's `Direction` + magnitude before this hook is called. */
function useAdjustSlots(
  tenantId: string,
): UseMutationResult<SlotMovement, ApiFailureError, AdjustSlotsInput> {
  const client = useApiClient();
  return useMutation<SlotMovement, ApiFailureError, AdjustSlotsInput>({
    mutationFn: ({ delta, reason }) =>
      client.postParsed(
        `/ops/tenants/${tenantId}/slot-movements`,
        { delta, reason },
        slotMovementSchema,
      ),
  });
}

function slotsLabel(n: number): string {
  return catalogue.plural('result.slots.other', n, { n: integer(n, getLocale()) });
}

/** `bg-primary-quiet`/`bg-destructive-quiet` are the same tone pair
 *  `Callout` uses for its neutral/blocked tones -- a positive delta reads as
 *  the same "fine" green, a negative one as the same "look at this" red,
 *  everywhere both appear. */
function DeltaChip(props: { delta: number }): ReactNode {
  const tone =
    props.delta < 0
      ? 'bg-destructive-quiet text-destructive-quiet-foreground'
      : 'bg-primary-quiet text-primary-quiet-foreground';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}
    >
      {props.delta > 0 ? `+${props.delta}` : props.delta}
    </span>
  );
}

type Grandeza = 'slots' | 'credits';

export function AdjustCapacity(props: {
  tenantId: string;
  tenantName: string;
  /** `null` when the tenant has no capacity row yet -- absence, never 0. */
  campaignSlots: number | null;
  /** `null` when the caller has no operator-readable source for it -- see
   *  the module doc. Never 0 standing in for "unknown". */
  campaignsInUse: number | null;
  /** Which face opens first -- the trigger that opened the dialog decides. */
  initialTab?: Grandeza;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): ReactNode {
  const [tab, setTab] = useState<Grandeza>(props.initialTab ?? 'slots');
  const queryClient = useQueryClient();
  const movementLegendId = useId();

  // -- Vagas de campanha face --
  // No default direction, the same guard the credits face states for its own
  // sign: a defaulted direction on a signed movement is the accident this
  // control exists to prevent.
  const [direction, setDirection] = useState<Direction | null>(null);
  const [magnitude, setMagnitude] = useState(1);
  const [reason, setReason] = useState('');
  const slotsAdjust = useAdjustSlots(props.tenantId);
  const reasonId = useId();
  const reasonErrorId = `${reasonId}-error`;
  const slotsFailure = slotsAdjust.error?.failure ?? null;
  const reasonError = fieldErrorOf(slotsFailure, 'reason');
  const deltaError = fieldErrorOf(slotsFailure, 'delta');

  // A reduction needs a base to reduce from -- absent, there is nothing to
  // subtract a slot out of yet.
  const canReduce = props.campaignSlots !== null;
  const delta = direction === 'decrease' ? -magnitude : magnitude;
  const after = props.campaignSlots === null ? null : props.campaignSlots + delta;
  // §5.1.1: a reduction below use is legal and never destructive -- every
  // campaign still resolves, only creation is refused. This warns, it does
  // not block. With no usage source (null), the warning cannot be computed,
  // so it is skipped rather than compared against a false 0.
  const overUse = props.campaignsInUse !== null && after !== null && after < props.campaignsInUse;

  const canSubmitSlots =
    direction !== null && magnitude > 0 && reason.trim() !== '' && !slotsAdjust.isPending;

  function submitSlots(): void | Promise<unknown> {
    if (!canSubmitSlots) return;
    return slotsAdjust.mutateAsync({ delta, reason: reason.trim() }).then(() => {
      void queryClient.invalidateQueries({ queryKey: opsKeys.tenant(props.tenantId) });
      setDirection(null);
      setMagnitude(1);
      setReason('');
    });
  }

  // -- Créditos face --
  // No default sign: a defaulted sign on a signed money form is the accident
  // this face exists to prevent.
  const [sign, setSign] = useState<Sign | null>(null);
  const [amount, setAmount] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const creditsAdjust = useAdjustCredits();
  const creditsFailure = creditsAdjust.error?.failure ?? null;
  const noteError = fieldErrorOf(creditsFailure, 'note');
  const amountError = fieldErrorOf(creditsFailure, 'credits');

  const canSubmitCredits =
    sign !== null &&
    amount !== null &&
    amount > 0 &&
    note.trim() !== '' &&
    !creditsAdjust.isPending;

  function submitCredits(): void | Promise<unknown> {
    if (!canSubmitCredits || amount === null || sign === null) return;
    const credits = sign === 'subtract' ? -amount : amount;
    return creditsAdjust
      .mutateAsync({ tenantId: props.tenantId, credits, note: note.trim() })
      .then(() => {
        void queryClient.invalidateQueries({ queryKey: opsKeys.tenant(props.tenantId) });
        void queryClient.invalidateQueries({ queryKey: opsKeys.movements(props.tenantId) });
        setSign(null);
        setAmount(null);
        setNote('');
      });
  }

  const active = tab === 'slots' ? slotsAdjust : creditsAdjust;

  return (
    <FormDialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      title={t('title')}
      description={props.tenantName}
      submitLabel={tab === 'slots' ? t('submit.slots') : t('submit.credits')}
      pending={active.isPending}
      failure={active.error}
      onSubmit={(event) => {
        event.preventDefault();
        return tab === 'slots' ? submitSlots() : submitCredits();
      }}
    >
      <Tabs value={tab} onValueChange={(value) => setTab(value as Grandeza)}>
        <TabsList aria-label={t('grandeza.legend')}>
          <TabsTrigger value="slots">{t('tab.slots')}</TabsTrigger>
          <TabsTrigger value="credits">{t('tab.credits')}</TabsTrigger>
        </TabsList>

        <TabsContent value="slots" className="flex flex-col gap-4">
          <FieldSet>
            <FieldLegend id={movementLegendId} variant="label">
              {t('movement.legend')}
            </FieldLegend>
            <div className="flex flex-wrap items-center gap-4">
              {/* `value=""` rather than `undefined`, so this control is
               *  controlled from its first render -- Radix warns on switching
               *  a `Tabs.Root` from uncontrolled to controlled, which passing
               *  `undefined` until a direction is chosen would do. No trigger
               *  below is ever given value `""`, so nothing shows selected
               *  until one is. */}
              <Tabs
                value={direction ?? ''}
                onValueChange={(value) => setDirection(value as Direction)}
              >
                <TabsList aria-labelledby={movementLegendId}>
                  <TabsTrigger value="increase">{t('direction.increase')}</TabsTrigger>
                  <TabsTrigger value="decrease" disabled={!canReduce}>
                    {t('direction.decrease')}
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="increase" tabIndex={-1} />
                <TabsContent value="decrease" tabIndex={-1} />
              </Tabs>
              <div className="flex items-center gap-1">
                <IconHint label={t('stepper.decrement')}>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setMagnitude((m) => Math.max(1, m - 1))}
                  >
                    <Minus aria-hidden />
                  </Button>
                </IconHint>
                <output aria-live="polite" className="w-10 text-center font-mono text-lg">
                  {magnitude}
                </output>
                <IconHint label={t('stepper.increment')}>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setMagnitude((m) => m + 1)}
                  >
                    <Plus aria-hidden />
                  </Button>
                </IconHint>
              </div>
            </div>
            {deltaError !== null && <FieldError>{deltaError}</FieldError>}
          </FieldSet>

          <div className="bg-muted flex items-center justify-between gap-4 rounded-lg p-lg">
            <div>
              <p className="text-caption text-muted-foreground">{t('result.now')}</p>
              <p className="text-title-md" data-testid="result-now">
                {props.campaignSlots === null ? (
                  <Absent reason={{ text: t('result.absent') }} />
                ) : (
                  slotsLabel(props.campaignSlots)
                )}
              </p>
            </div>
            <ChevronRight aria-hidden className="text-muted-foreground shrink-0" />
            <div>
              <p className="text-caption text-muted-foreground">{t('result.after')}</p>
              <p className="text-title-md" data-testid="result-after">
                {after === null ? (
                  <Absent reason={{ text: t('result.absent') }} />
                ) : (
                  slotsLabel(after)
                )}
              </p>
            </div>
            {direction !== null && <DeltaChip delta={delta} />}
          </div>

          {props.campaignsInUse === null ? (
            <Callout tone="neutral">{t('warning.unknown')}</Callout>
          ) : (
            overUse &&
            after !== null && (
              <Callout tone="blocked">
                {catalogue.plural('warning.other', props.campaignsInUse, {
                  inUse: integer(props.campaignsInUse, getLocale()),
                  after: integer(after, getLocale()),
                })}
              </Callout>
            )
          )}

          <Field data-invalid={reasonError !== null || undefined}>
            <FieldLabel htmlFor={reasonId}>{t('reason.label')}</FieldLabel>
            <Input
              id={reasonId}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              {...fieldControlProps(slotsFailure, 'reason', reasonErrorId)}
            />
            <FieldError id={reasonErrorId}>{reasonError}</FieldError>
          </Field>
        </TabsContent>

        <TabsContent value="credits" className="flex flex-col gap-4">
          <AmountField
            sign={sign}
            onSignChange={setSign}
            value={amount}
            onChange={setAmount}
            hint={t('credits.amount.hint')}
            error={amountError}
          />
          <Field data-invalid={noteError !== null || undefined}>
            <FieldLabel htmlFor="capacity-credits-note">{t('credits.note.label')}</FieldLabel>
            <Input
              id="capacity-credits-note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              {...fieldControlProps(creditsFailure, 'note', 'capacity-credits-note-error')}
            />
            <FieldDescription>{t('credits.note.hint')}</FieldDescription>
            <FieldError id="capacity-credits-note-error">{noteError}</FieldError>
          </Field>
        </TabsContent>
      </Tabs>
    </FormDialog>
  );
}
